import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import client from "@/api/client";
import { BulkToolbar, useBulkSelection } from "@/components/admin/BulkToolbar";

export function AdminContactManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/contact-submissions");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const bulk = useBulkSelection("contact_submissions", (it) => it.submission_id, load);
  const allSelected = items.length > 0 && items.every((m) => bulk.isSelected(m.submission_id));

  const single = async (id, action) => {
    try {
      if (action === "delete") await client.delete(`/admin/contact-submissions/${id}`);
      else if (action === "mark-read") await client.post(`/admin/contact-submissions/${id}/mark-read`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const bulkActions = [
    { key: "mark_read", label: t("admin.markRead"), icon: <Mail className="h-3.5 w-3.5" /> },
    {
      key: "delete",
      label: t("admin.delete"),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: "destructive",
      requiresConfirm: true,
      confirmTitle: "Delete selected messages?",
      confirmBody: "This removes the messages permanently and cannot be undone.",
    },
  ];

  return (
    <Card className="rounded-2xl border border-border p-6 space-y-4" data-testid="admin-panel-contact">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">{t("admin.contactSubmissions")}</h2>
        <Button variant="outline" size="sm" className="rounded-xl" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.tryAgain")}
        </Button>
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => bulk.toggleAll(items)}
            aria-label={t("common.selectAll")}
            data-testid="admin-contact-select-all"
          />
          {t("common.selectAllPage")}
        </div>
      )}

      <BulkToolbar {...bulk} actions={bulkActions} testId="admin-contact-bulk" onClear={bulk.clear} />

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="admin-contact-empty">{t("admin.contactEmpty")}</p>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div
              key={m.submission_id}
              className={`rounded-xl border p-4 ${bulk.isSelected(m.submission_id) ? "border-primary/70 bg-primary/5" : "border-border"}`}
              data-testid="admin-contact-row"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={bulk.isSelected(m.submission_id)}
                    onChange={() => bulk.toggle(m.submission_id)}
                    aria-label={`Select ${m.email}`}
                    data-testid={`admin-contact-select-${m.submission_id}`}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium">{m.full_name} {m.organization && <span className="text-muted-foreground">· {m.organization}</span>}</p>
                    <p className="text-xs text-muted-foreground font-mono">{m.email} · {m.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="rounded-full capitalize">
                    {m.status === "read" ? t("admin.read") : t("admin.new")}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{m.created_at ? format(parseISO(m.created_at), "MMM d, HH:mm") : ""}</span>
                </div>
              </div>
              <p className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>
              <div className="mt-3 flex items-center gap-1 justify-end">
                {m.status !== "read" && (
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => single(m.submission_id, "mark-read")} data-testid="admin-contact-mark-read">
                    <Mail className="h-3.5 w-3.5" /> {t("admin.markRead")}
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => single(m.submission_id, "delete")}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default AdminContactManager;
